import {
  Box,
  Button,
  Grid,
  Heading,
  HStack,
  Icon,
  Image,
  Link,
  SimpleGrid,
  Text,
  VStack,
} from '@chakra-ui/react';
import { FaLinkedinIn } from 'react-icons/fa';
import { FaXTwitter } from 'react-icons/fa6';
import { Link as RouterLink } from 'react-router-dom';
import MarketingPageShell, {
  breadcrumbSchema,
} from '../components/MarketingPageShell';
import { ABOUT_PILLARS } from '../content/aboutPillars';
import { SITE_URL } from '../components/PageMeta';
import { buildSignupUrl, trackSignupClick } from '../utils/analytics';

const APP_URL = 'https://app.wagerwatchdog.com';
const SIGNUP_URL = `${APP_URL}/signup`;

const BREADCRUMBS = [
  { name: 'Home', path: '/' },
  { name: 'About', path: '/about' },
];

const ARBITER_CAPABILITIES = [
  {
    title: 'Source ingestion',
    body: 'We monitor the channels experts already use—Discord, Twitter/X, newsletters, Telegram, and more—and capture public and paid picks as they are published, not after the fact.',
  },
  {
    title: 'Extraction & detection',
    body: 'Text, images, and slips are parsed into structured picks: player or team, stat category, line, and over/under. Parlays are detected and tracked leg by leg.',
  },
  {
    title: 'Normalization',
    body: 'Raw names are matched to real players, teams, sports, and seasons so “Ja Morant assists” and “Morant o4.5 ast” resolve to the same measurable bet.',
  },
  {
    title: 'Game & stat matching',
    body: 'Every pick is tied to an actual game and stat category, then graded against official results once games settle—wins, losses, and pushes included.',
  },
  {
    title: 'Parlay resolution',
    body: 'Multi-leg parlays stay linked from detection through scoring. We know when every leg is in and when the full ticket is ready to grade.',
  },
  {
    title: 'Ratings & analytics',
    body: 'Outcomes roll up into independent expert ratings—WCS for win-rate skill, PR and ROI for modeled tailing profit—plus consensus, hot picks, and sport-level breakdowns.',
  },
];

const FOUNDERS = [
  {
    name: 'Joe Schindler',
    role: 'CEO/CTO',
    image: '/assets/images/founders/joe-schindler.png',
    linkedin: 'https://www.linkedin.com/in/joseph-schindler-9bb315178/',
    bio: 'Joe is CEO/CTO of WagerWatchdog and architect of our in-house proprietary engine—the system behind global pick collection, analysis, and independent expert ratings. He is currently a Senior Software Engineer at Recorded Future/Mastercard and was previously a software automation lead for DENSO North America. He has a BS in Mechanical Engineering with a minor in Aerospace Engineering.',
  },
  {
    name: 'Jared Smith, PhD',
    role: 'COO/CPO',
    image: '/assets/images/founders/jared-smith.png',
    linkedin: 'https://linkedin.com/in/jaredthecoder',
    x: 'https://x.com/jaredthecoder',
    bio: 'Jared is COO/CPO of WagerWatchdog, Co-founder/CTO of Uncat.com, and Chief Scientist at HUMAN Security—whose technology powers bot and fraud defense for 19 of the top 20 retail sites and adtech companies. Previously a Senior Engineering Manager at Mastercard, Distinguished Engineer and Senior Director at SecurityScorecard, and was the Lead Scientist for AI in Cybersecurity in the US DOE. He has a PhD in Computer Science, 7 patents, 20+ published papers, and 30+ invited talks globally.',
  },
];

const FAQS = [
  {
    q: 'Is WagerWatchdog a sportsbook?',
    a: 'No. We are a sports betting analytics platform. We do not accept wagers, place bets for you, or operate as a bookmaker.',
  },
  {
    q: 'Who is WagerWatchdog for?',
    a: 'Bettors and researchers who want verified expert performance data. Win rates, modeled profit metrics, and consensus.  Not cherry-picked records or unverified tip-sheet claims.',
  },
  {
    q: 'What does WagerWatchdog track?',
    a: 'Expert picks across major sports, scored against outcomes with transparent analytics. We turn noisy expert content into structured, auditable performance data.',
  },
  {
    q: 'What is WCS?',
    a: 'WCS is the WagerWatchdog Credibility Score—our proprietary rating for long-term expert performance. It weighs consistency, sample size, market performance, and historical results. Higher WCS means more trustworthy long-term performance, not just a hot week.',
  },
  {
    q: 'How do you collect expert picks?',
    a: 'We monitor the channels experts already use—Discord, Twitter/X, newsletters, Telegram, and more—and capture public and paid picks as they are published. We do not rely on self-reported records or cherry-picked screenshots submitted after the fact.',
  },
  {
    q: 'Can betting experts claim their profile?',
    a: 'Yes. Use Claim Your Profile on the site (or email claim@wagerwatchdog.com) to verify ownership. Once verified, you can connect your brand to your tracked record and manage how you appear on the platform.',
  },
];

const ABOUT_DESCRIPTION =
  'Learn about WagerWatchdog, the sports betting analytics company that tracks expert picks with transparent scoring, WCS, PR, and ROI—so you can see who actually wins.';

const ABOUT_SCHEMA = [
  {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: 'About WagerWatchdog',
    description: ABOUT_DESCRIPTION,
    url: `${SITE_URL}/about`,
    isPartOf: {
      '@type': 'WebSite',
      name: 'WagerWatchdog',
      url: SITE_URL,
    },
    about: {
      '@type': 'Organization',
      name: 'IvoryShoulder, LLC',
      alternateName: 'WagerWatchdog',
      url: SITE_URL,
      description:
        'Sports betting analytics platform that tracks expert performance with real-time pick tracking and transparent scoring.',
      email: 'support@wagerwatchdog.com',
      sameAs: [
        'https://discord.com/invite/MUpH2jqg94',
        'https://app.wagerwatchdog.com',
      ],
    },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQS.map(({ q, a }) => ({
      '@type': 'Question',
      name: q,
      acceptedAnswer: { '@type': 'Answer', text: a },
    })),
  },
  {
    '@context': 'https://schema.org',
    ...breadcrumbSchema(BREADCRUMBS),
  },
];

/**
 * About Us — edit copy in this file to update the marketing site.
 * SEO focus keyword: sports betting analytics company / about WagerWatchdog
 */
export default function AboutUs() {
  return (
    <MarketingPageShell
      fullTitle="About WagerWatchdog | Sports Betting Analytics Company"
      description={ABOUT_DESCRIPTION}
      schema={ABOUT_SCHEMA}
      breadcrumbs={BREADCRUMBS}
      h1="About"
      h1Accent="WagerWatchdog"
      lead="There are a lot of “experts” posting picks. Almost none of them have a record you can independently verify.

            WagerWatchdog is that record. We track handicappers across major sports, ingest what they actually post, and grade every pick against the outcome. No self-reported screenshots. No deleted losses.

            If you want to track proven records and niche experts, check out WagerWatchdog."
    >
      <HStack
        spacing={{ base: 6, md: 10 }}
        align={{ base: 'start', md: 'center' }}
        flexDir={{ base: 'column', md: 'row' }}
      >
        <Image
          src="/assets/images/watchdog_logo_dog_white.svg"
          alt="WagerWatchdog mascot"
          h={{ base: '96px', md: '128px' }}
          w="auto"
          objectFit="contain"
          flexShrink={0}
          sx={{ filter: 'drop-shadow(0 10px 28px rgba(0, 0, 0, 0.45))' }}
        />
        <VStack align="start" spacing={4} maxW="640px">
          <Heading as="h2" fontSize={{ base: 'xl', md: '2xl' }} color="white">
            Who we are
          </Heading>
          <Text color="gray.300" lineHeight="1.8" fontSize={{ base: 'md', md: 'lg' }}>
          The first and only true 3rd-party tipster/influencer performance tracking and analytics platform.
          </Text>
          <Text color="gray.400" lineHeight="1.8">
            Sports betting advice is everywhere. Most of it is hard to verify. We
            exist so you can see who actually wins, what is working, and where
            consensus is forming.
          </Text>
        </VStack>
      </HStack>

      <Box>
        <Heading
          as="h2"
          fontSize={{ base: 'xl', md: '2xl' }}
          color="white"
          mb={6}
        >
          What we believe
        </Heading>
        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={5}>
          {ABOUT_PILLARS.map((pillar) => (
            <Box
              key={pillar.title}
              p={{ base: 6, md: 7 }}
              bg="gray.800"
              borderRadius="16px"
              border="1px solid"
              borderColor="whiteAlpha.100"
              minH={{ base: 'auto', md: '220px' }}
              transition="all 0.2s"
              _hover={{
                borderColor: 'primary.500',
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 30px rgba(10, 112, 185, 0.12)',
              }}
            >
              <VStack align="start" spacing={4}>
                <Icon viewBox="0 0 24 24" boxSize={8} color={pillar.color}>
                  <path fill="currentColor" d={pillar.iconPath} />
                </Icon>
                <Heading as="h3" size="md" color="white">
                  {pillar.title}
                </Heading>
                <Text color="gray.400" lineHeight="1.7" fontSize="sm">
                  {pillar.body}
                </Text>
              </VStack>
            </Box>
          ))}
        </SimpleGrid>
      </Box>

      <Box
        p={{ base: 6, md: 8 }}
        borderRadius="16px"
        border="1px solid"
        borderColor="whiteAlpha.100"
        bg="linear-gradient(135deg, rgba(10,112,185,0.12) 0%, rgba(31,41,55,0.9) 55%)"
      >
        <Heading as="h2" fontSize={{ base: 'xl', md: '2xl' }} color="white" mb={4}>
          What we build
        </Heading>
        <Text color="gray.300" lineHeight="1.8" mb={3}>
          WagerWatchdog runs on an in-house proprietary engine built for one job:
          turn expert posts into independently verified performance data. It does
          not rely on handicappers self-reporting records or posting cherry-picked
          screenshots. It ingests what experts actually publish, structures it,
          matches it to real games, and scores every outcome—at scale,
          continuously, and independently.
        </Text>
        <Text color="gray.400" lineHeight="1.8" mb={6}>
          Thousands of picks flow through the same pipeline every week. The same
          rules apply to everyone. That is what makes the ratings defensible.
        </Text>
        <Heading
          as="h3"
          fontSize={{ base: 'lg', md: 'xl' }}
          color="white"
          mb={4}
        >
          How our engine turns posts into ratings
        </Heading>
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
          {ARBITER_CAPABILITIES.map((item) => (
            <Box
              key={item.title}
              p={5}
              bg="gray.800"
              borderRadius="12px"
              border="1px solid"
              borderColor="whiteAlpha.100"
            >
              <Heading as="h4" fontSize="md" color="primary.300" mb={2}>
                {item.title}
              </Heading>
              <Text color="gray.400" lineHeight="1.7" fontSize="sm">
                {item.body}
              </Text>
            </Box>
          ))}
        </SimpleGrid>
        <Text color="gray.500" lineHeight="1.7" fontSize="sm" mt={6}>
          The result is a living record of expert performance you can browse,
          compare, and trust—built on verified picks and outcomes, not marketing.
        </Text>
      </Box>

      <Box>
        <Heading
          as="h2"
          fontSize={{ base: '2xl', md: '3xl' }}
          fontWeight="extrabold"
          textAlign="center"
          letterSpacing="0.04em"
          textTransform="uppercase"
          mb={{ base: 6, md: 8 }}
        >
          Meet the{' '}
          <Text as="span" color="primary.400">
            Founders
          </Text>
        </Heading>
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={5}>
          {FOUNDERS.map((founder) => (
            <Box
              key={founder.name}
              p={{ base: 5, md: 7 }}
              borderRadius="16px"
              border="1px solid"
              borderColor="whiteAlpha.100"
              bg="linear-gradient(145deg, rgba(10,112,185,0.22) 0%, rgba(17,24,39,0.96) 55%)"
              boxShadow="0 12px 40px rgba(0, 0, 0, 0.25)"
              minH={{ base: 'auto', md: '320px' }}
              display="flex"
              flexDirection="column"
            >
              <HStack align="start" spacing={5} mb={5}>
                <Image
                  src={founder.image}
                  alt={founder.name}
                  boxSize={{ base: '88px', md: '104px' }}
                  borderRadius="full"
                  objectFit="cover"
                  flexShrink={0}
                  border="2px solid"
                  borderColor="whiteAlpha.300"
                />
                <VStack align="start" spacing={2} flex="1" minW={0} pt={1}>
                  <Heading
                    as="h3"
                    fontSize={{ base: 'lg', md: 'xl' }}
                    fontWeight="extrabold"
                    color="white"
                    textTransform="uppercase"
                    letterSpacing="0.03em"
                    lineHeight="short"
                  >
                    {founder.name}
                  </Heading>
                  <Text
                    fontSize={{ base: 'md', md: 'lg' }}
                    fontWeight="bold"
                    color="primary.300"
                    textTransform="uppercase"
                    letterSpacing="wider"
                    lineHeight="short"
                  >
                    {founder.role}
                  </Text>
                </VStack>
              </HStack>
              <Text
                color="gray.300"
                lineHeight="1.8"
                fontSize="sm"
                flex="1"
                mb={4}
              >
                {founder.bio}
              </Text>
              <HStack spacing={1}>
                <Link
                  href={founder.linkedin}
                  isExternal
                  target="_blank"
                  rel="noopener noreferrer"
                  display="inline-flex"
                  alignItems="center"
                  justifyContent="center"
                  w="36px"
                  h="36px"
                  borderRadius="md"
                  color="gray.300"
                  _hover={{ color: 'white', bg: 'whiteAlpha.100', textDecoration: 'none' }}
                  aria-label={`${founder.name} on LinkedIn`}
                >
                  <Icon as={FaLinkedinIn} boxSize={5} />
                </Link>
                {'x' in founder && founder.x ? (
                  <Link
                    href={founder.x}
                    isExternal
                    target="_blank"
                    rel="noopener noreferrer"
                    display="inline-flex"
                    alignItems="center"
                    justifyContent="center"
                    w="36px"
                    h="36px"
                    borderRadius="md"
                    color="gray.300"
                    _hover={{ color: 'white', bg: 'whiteAlpha.100', textDecoration: 'none' }}
                    aria-label={`${founder.name} on X`}
                  >
                    <Icon as={FaXTwitter} boxSize={5} />
                  </Link>
                ) : null}
              </HStack>
            </Box>
          ))}
        </SimpleGrid>
      </Box>

      <Box>
        <Heading
          as="h2"
          fontSize={{ base: 'xl', md: '2xl' }}
          color="white"
          mb={6}
        >
          Common questions
        </Heading>
        <Grid templateColumns={{ base: '1fr', md: '1fr 1fr' }} gap={5}>
          {FAQS.map((item) => (
            <Box
              key={item.q}
              p={6}
              bg="gray.800"
              borderRadius="12px"
              border="1px solid"
              borderColor="whiteAlpha.100"
            >
              <Heading as="h3" fontSize="md" color="white" mb={3}>
                {item.q}
              </Heading>
              <Text color="gray.400" lineHeight="1.7" fontSize="sm">
                {item.a}
              </Text>
            </Box>
          ))}
        </Grid>
      </Box>

      <Box
        pt={2}
        display="flex"
        flexDir={{ base: 'column', sm: 'row' }}
        gap={3}
        alignItems={{ base: 'stretch', sm: 'center' }}
        flexWrap="wrap"
      >
        <Button
          as={Link}
          href={buildSignupUrl(SIGNUP_URL)}
          variant="primary"
          size="lg"
          minH="48px"
          _hover={{ textDecoration: 'none' }}
          onClick={() => trackSignupClick('about_cta')}
        >
          Get started
        </Button>
        <Button
          as={RouterLink}
          to="/claim"
          variant="outline"
          size="lg"
          minH="48px"
          _hover={{ textDecoration: 'none' }}
        >
          Claim your profile
        </Button>
        <Button
          as={RouterLink}
          to="/contact"
          variant="outline"
          size="lg"
          minH="48px"
          _hover={{ textDecoration: 'none' }}
        >
          Contact us
        </Button>
        <Button
          as={RouterLink}
          to="/responsible-gaming"
          variant="ghost"
          size="lg"
          minH="48px"
          color="gray.400"
          _hover={{ textDecoration: 'none', color: 'white', bg: 'gray.800' }}
        >
          Responsible Gaming
        </Button>
      </Box>
    </MarketingPageShell>
  );
}
